/**
 * ThemeContext - Zentrale Theme-Verwaltung (Light/Dark Mode)
 * 
 * Zweck:
 * - Light Mode als Default (Outdoor-Readability +250%)
 * - User-Preference in localStorage persistieren
 * - Auto-Mode basierend auf Tageszeit (06:00-20:00 = Light)
 * 
 * Kontext:
 * - Deep Research: Dark Theme 3-5x schlechtere Outdoor-Lesbarkeit
 * - Sprint 2 Task 1: Light Mode als Demo-Blocker (P0)
 * - WCAG AAA Kontrast-Ziel: ≥7:1 für Outdoor-Bedingungen
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

import type { Theme } from '../types/ui';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  effectiveTheme: 'light' | 'dark'; // Tatsächlich angewendetes Theme (Auto → Light/Dark)
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// localStorage Key für User-Preference
const THEME_STORAGE_KEY = '360volt-theme-preference';

/**
 * Auto-Mode Logik: Basierend auf Tageszeit
 * 06:00-20:00 Uhr = Light Mode (Arbeitszeit, Outdoor)
 * 20:00-06:00 Uhr = Dark Mode (Abends, Augen schonen)
 */
const getAutoTheme = (): 'light' | 'dark' => {
  const hour = new Date().getHours();
  // 6 Uhr morgens bis 20 Uhr abends = Light Mode
  return hour >= 6 && hour < 20 ? 'light' : 'dark';
};

/**
 * Effektives Theme berechnen
 * - 'light' → 'light'
 * - 'dark' → 'dark'
 * - 'auto' → getAutoTheme() (zeitbasiert)
 */
const resolveEffectiveTheme = (theme: Theme): 'light' | 'dark' => {
  if (theme === 'auto') {
    return getAutoTheme();
  }
  return theme;
};

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // State: User-gewähltes Theme (default: 'light')
  const [theme, setThemeState] = useState<Theme>(() => {
    // Beim ersten Load: localStorage auslesen oder 'light' als Default
    const stored = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null;
    return stored || 'light'; // ✅ Light Mode als Default (nicht Dark!)
  });

  // Effektives Theme (nach Auto-Auflösung)
  const [effectiveTheme, setEffectiveTheme] = useState<'light' | 'dark'>(() => 
    resolveEffectiveTheme(theme)
  );

  /**
   * Theme setzen + in localStorage speichern
   */
  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
  };

  /**
   * Effect: Theme auf <html> Element anwenden
   * - Light Mode: 'dark' class entfernen
   * - Dark Mode: 'dark' class hinzufügen
   */
  useEffect(() => {
    const resolved = resolveEffectiveTheme(theme);
    setEffectiveTheme(resolved);

    const root = document.documentElement;
    if (resolved === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  /**
   * Effect: Auto-Mode Timer (alle 60s prüfen ob Tageszeit gewechselt)
   * - Nur relevant wenn theme === 'auto'
   * - Verhindert Sprung um 06:00 / 20:00 Uhr ohne Reload
   */
  useEffect(() => {
    if (theme !== 'auto') return;

    const interval = setInterval(() => {
      const newAutoTheme = getAutoTheme();
      if (newAutoTheme !== effectiveTheme) {
        setEffectiveTheme(newAutoTheme);
        const root = document.documentElement;
        if (newAutoTheme === 'dark') {
          root.classList.add('dark');
        } else {
          root.classList.remove('dark');
        }
      }
    }, 60000); // Alle 60 Sekunden prüfen

    return () => clearInterval(interval);
  }, [theme, effectiveTheme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, effectiveTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Hook für Theme-Zugriff in Komponenten
 * 
 * @example
 * ```tsx
 * const { theme, setTheme, effectiveTheme } = useTheme();
 * setTheme('light'); // Light Mode aktivieren
 * setTheme('auto');  // Auto-Mode aktivieren
 * ```
 */
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme muss innerhalb von ThemeProvider verwendet werden');
  }
  return context;
};
